import { gql, type ApolloClient } from "@apollo/client";
import { getAccessToken } from "services/auth";
import CIQClient, { HTTPClientResponse } from "services/ciq-client";
import { fileWithNormalizedClassificationCodes } from "./normalize-design-scope-import-csv";
import {
  BOOTSTRAP_FEATURE_CATALOG,
  DELETE_FEATURE_CATALOG,
  DELETE_FEATURE_CATALOG_ITEM,
  GET_FEATURE_CATALOG_ITEMS,
  GET_FEATURE_CATALOGS
} from "./graphql-queries-mutations";

export {
  BOOTSTRAP_FEATURE_CATALOG,
  GET_FEATURE_CATALOG_ITEMS,
  GET_FEATURE_CATALOGS
} from "./graphql-queries-mutations";

const DESIGN_SCOPE_FEATURE_TYPE_ID = 10;

function textFromBackend(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  return text || undefined;
}

/** Backend text only. Thrown client errors and missing payloads stay unset. */
function backendErrorMessage(
  error: unknown,
  graphqlMessage?: string | null
): string | undefined {
  const fromGraphql = textFromBackend(graphqlMessage);
  if (fromGraphql) return fromGraphql;
  if (!error || typeof error !== "object") return undefined;

  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ message?: string | null }> }
  ).graphQLErrors;
  const fromList = graphQLErrors
    ?.map((item) => textFromBackend(item?.message))
    .find((item): item is string => Boolean(item));
  if (fromList) return fromList;

  const data = (
    error as { response?: { data?: { error?: unknown; message?: unknown } } }
  ).response?.data;
  return textFromBackend(data?.error) ?? textFromBackend(data?.message);
}

const DESIGN_SCOPE_IMPORT_TEMPLATE_URL =
  "/features/DESIGN_SCOPE/import-template?scope=subscription";

export const DESIGN_SCOPE_IMPORT_TEMPLATE_FILENAME =
  "Design_Scope_Standard_Format.csv";

/**
 * Download the subscription-level Design Scope CSV import template.
 * Auth: subscription access token (Subscription Admin / Editor).
 */
export async function downloadDesignScopeImportTemplate(): Promise<HTTPClientResponse> {
  const subscriptionToken = getAccessToken();
  return CIQClient.get({
    url: DESIGN_SCOPE_IMPORT_TEMPLATE_URL,
    showError: false,
    config: {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${subscriptionToken}`
      }
    }
  });
}

export type ImportDesignScopeCatalogParams = {
  file: File;
  catalogName: string;
  description?: string;
};

/** Body from POST /features/10/catalogs/import. The call finishes the import. */
export type ImportDesignScopeCatalogResponse = {
  catalogId: string;
  isNewCatalog: boolean;
  total: number;
  inserted: number;
  updated: number;
  corrected: number;
  failed: number;
  errorReportUrl: string | null;
};

export type DesignScopeCatalog = {
  id: string;
  subscriptionId: string;
  name: string;
  description: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  itemCount: number | null;
  addedBy: string | null;
  addedOn: string | null;
};

export type DesignScopeCatalogUpdate = {
  name?: string;
  description?: string | null;
};

function updateFeatureCatalogMutation(patch: DesignScopeCatalogUpdate) {
  const variableDefinitions = [
    patch.name !== undefined ? "$name: String" : "",
    patch.description !== undefined ? "$description: String" : ""
  ].filter(Boolean);
  const argumentsList = [
    patch.name !== undefined ? "name: $name" : "",
    patch.description !== undefined ? "description: $description" : ""
  ].filter(Boolean);

  return gql`
    mutation UpdateFeatureCatalog(
      $feature_type_id: Int!
      $catalog_id: uuid!
      ${variableDefinitions.join("\n")}
    ) {
      update_feature_catalog(
        feature_type_id: $feature_type_id
        catalog_id: $catalog_id
        ${argumentsList.join("\n")}
      ) {
        message
        result {
          id
          name
          description
          updated_at
          updated_by
        }
      }
    }
  `;
}

/**
 * Update a catalog name and/or description.
 * Send only the fields in `patch`.
 */
export async function updateDesignScopeCatalog(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogId: string,
  patch: DesignScopeCatalogUpdate
): Promise<HTTPClientResponse> {
  try {
    const result = await client.mutate<{
      update_feature_catalog?: {
        message?: string | null;
        result?: {
          id: string;
          name: string;
          description: string | null;
          updated_at?: string | null;
          updated_by?: string | null;
        } | null;
      } | null;
    }>({
      mutation: updateFeatureCatalogMutation(patch),
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId,
        ...patch
      }
    });

    const graphqlError = result.errors?.[0];
    const updated = result.data?.update_feature_catalog?.result;
    if (graphqlError || !updated?.id) {
      return {
        success: false,
        errorMsg: backendErrorMessage(
          undefined,
          graphqlError?.message || result.data?.update_feature_catalog?.message
        ),
        response: result
      };
    }

    return {
      success: true,
      data: updated,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

/**
 * Soft-delete a Design Scope catalog and its items.
 */
export async function deleteDesignScopeCatalog(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogId: string
): Promise<HTTPClientResponse> {
  try {
    const result = await client.mutate<{
      delete_feature_catalog?: { catalog_id: string } | null;
    }>({
      mutation: DELETE_FEATURE_CATALOG,
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId
      }
    });

    const graphqlError = result.errors?.[0];
    const deleted = result.data?.delete_feature_catalog;
    if (graphqlError || !deleted?.catalog_id) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: deleted,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

/**
 * Soft-delete one item in a Design Scope catalog.
 */
export async function deleteDesignScopeCatalogItem(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogId: string,
  itemId: string
): Promise<HTTPClientResponse> {
  try {
    const result = await client.mutate<{
      delete_feature_catalog_item?: { item_id: string } | null;
    }>({
      mutation: DELETE_FEATURE_CATALOG_ITEM,
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId,
        item_id: itemId
      }
    });

    const graphqlError = result.errors?.[0];
    const deleted = result.data?.delete_feature_catalog_item;
    if (graphqlError || !deleted?.item_id) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: deleted,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

export type DesignScopeCatalogItemUpdate = {
  title?: string;
  description?: string;
  uniformat?: string;
  masterformat?: string;
};

function updateFeatureCatalogItemMutation(patch: DesignScopeCatalogItemUpdate) {
  const variableDefinitions = [
    patch.title !== undefined ? "$title: String" : "",
    patch.description !== undefined ? "$description: String" : "",
    patch.uniformat !== undefined ? "$uniformat: String" : "",
    patch.masterformat !== undefined ? "$masterformat: String" : ""
  ].filter(Boolean);
  const argumentsList = [
    patch.title !== undefined ? "title: $title" : "",
    patch.description !== undefined ? "description: $description" : "",
    patch.uniformat !== undefined ? "uniformat: $uniformat" : "",
    patch.masterformat !== undefined ? "masterformat: $masterformat" : ""
  ].filter(Boolean);

  return gql`
    mutation UpdateFeatureCatalogItem(
      $feature_type_id: Int!
      $catalog_id: uuid!
      $item_id: uuid!
      ${variableDefinitions.join("\n")}
    ) {
      update_feature_catalog_item(
        feature_type_id: $feature_type_id
        catalog_id: $catalog_id
        item_id: $item_id
        ${argumentsList.join("\n")}
      ) {
        id
        title
        description
      }
    }
  `;
}

/**
 * Update one field on a catalog item.
 * `patch` contains only the field the user edited.
 */
export async function updateDesignScopeCatalogItem(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogId: string,
  itemId: string,
  patch: DesignScopeCatalogItemUpdate
): Promise<HTTPClientResponse> {
  try {
    const result = await client.mutate<{
      update_feature_catalog_item?: { id: string } | null;
    }>({
      mutation: updateFeatureCatalogItemMutation(patch),
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId,
        item_id: itemId,
        ...patch
      }
    });

    const graphqlError = result.errors?.[0];
    const updated = result.data?.update_feature_catalog_item;
    if (graphqlError || !updated?.id) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: updated,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

export type DesignScopeCatalogItemInput = {
  title: string;
  description?: string;
  uniformat?: string;
  masterformat?: string;
};

function addFeatureCatalogItemMutation(input: DesignScopeCatalogItemInput) {
  const variableDefinitions = [
    input.description !== undefined ? "$description: String" : "",
    input.uniformat !== undefined ? "$uniformat: String" : "",
    input.masterformat !== undefined ? "$masterformat: String" : ""
  ].filter(Boolean);
  const argumentsList = [
    input.description !== undefined ? "description: $description" : "",
    input.uniformat !== undefined ? "uniformat: $uniformat" : "",
    input.masterformat !== undefined ? "masterformat: $masterformat" : ""
  ].filter(Boolean);

  return gql`
    mutation AddFeatureCatalogItem(
      $feature_type_id: Int!
      $catalog_id: uuid!
      $title: String!
      ${variableDefinitions.join("\n")}
    ) {
      add_feature_catalog_item(
        feature_type_id: $feature_type_id
        catalog_id: $catalog_id
        title: $title
        ${argumentsList.join("\n")}
      ) {
        id
        title
        description
      }
    }
  `;
}

/**
 * Add one item to a Design Scope catalog.
 * Optional fields are omitted from the mutation when they are not set.
 */
export async function addDesignScopeCatalogItem(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogId: string,
  input: DesignScopeCatalogItemInput
): Promise<HTTPClientResponse> {
  const { title, ...optional } = input;
  try {
    const result = await client.mutate<{
      add_feature_catalog_item?: { id: string } | null;
    }>({
      mutation: addFeatureCatalogItemMutation(input),
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId,
        title,
        ...optional
      }
    });

    const graphqlError = result.errors?.[0];
    const created = result.data?.add_feature_catalog_item;
    if (graphqlError || !created?.id) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: created,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

/**
 * List active Design Scope catalogs for the current subscription.
 * `get_feature_catalogs` already excludes soft-deleted catalogs.
 */
export async function listDesignScopeCatalogs(
  client: Pick<ApolloClient<unknown>, "query">
): Promise<HTTPClientResponse> {
  try {
    const result = await client.query<{
      get_feature_catalogs?: DesignScopeCatalog[] | null;
    }>({
      query: GET_FEATURE_CATALOGS,
      variables: { feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID },
      fetchPolicy: "network-only"
    });

    const catalogs = result.data?.get_feature_catalogs;
    const graphqlError = result.errors?.[0];
    if (graphqlError || !Array.isArray(catalogs)) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: catalogs,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

export type BootstrapFeatureCatalogResult = {
  importLogId: string | null;
  catalogId: string;
  catalogName: string | null;
  created: number;
  skipped: number;
  status: string | null;
  error: string | null;
};

/**
 * Seed the open project from one or more Design Scope catalogs.
 * The mutation finishes the bootstrap and returns per-catalog results.
 */
export async function bootstrapDesignScopeFromCatalogs(
  client: Pick<ApolloClient<unknown>, "mutate">,
  catalogIds: string[]
): Promise<HTTPClientResponse> {
  try {
    const result = await client.mutate<{
      bootstrap_feature_catalog?: {
        results?: BootstrapFeatureCatalogResult[] | null;
      } | null;
    }>({
      mutation: BOOTSTRAP_FEATURE_CATALOG,
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalogIds
      }
    });

    const graphqlError = result.errors?.[0];
    const results = result.data?.bootstrap_feature_catalog?.results;
    if (graphqlError || !Array.isArray(results)) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    return {
      success: true,
      data: results,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

export type DesignScopeCatalogClassificationPathNode = {
  number: string;
  title: string;
};

export type DesignScopeCatalogItemClassification = {
  id: string | number;
  name: string;
  code: string;
  system: "UNIFORMAT" | "MASTERFORMAT";
  parentId?: string | null;
  /** Ancestor chain (root first); may omit the leaf code itself. */
  path?: DesignScopeCatalogClassificationPathNode[];
};

export type DesignScopeCatalogItem = {
  id: string;
  title: string;
  description: string;
  number: string;
  classifications: DesignScopeCatalogItemClassification[];
};

type FeatureCatalogClassificationRef = {
  id: number | null;
  number: string | null;
  title: string | null;
};

type FeatureCatalogClassificationRow = {
  id: number | string | null;
  number: string | null;
  title: string | null;
  parent_id?: number | string | null;
  path?: Array<{ number: string | null; title: string | null }> | null;
  system: string | null;
};

type FeatureCatalogItemRow = {
  id: string;
  sort_no: number | null;
  title: string;
  description: string | null;
  deleted: boolean | null;
  uniformat: FeatureCatalogClassificationRef | null;
  masterformat: FeatureCatalogClassificationRef | null;
  classifications?: FeatureCatalogClassificationRow[] | null;
};

function mapCatalogClassificationSystem(
  system: string | null | undefined
): DesignScopeCatalogItemClassification["system"] | null {
  if (system === "uni-format" || system === "UNIFORMAT" || system === "UF") {
    return "UNIFORMAT";
  }
  if (
    system === "master-format" ||
    system === "MASTERFORMAT" ||
    system === "MF"
  ) {
    return "MASTERFORMAT";
  }
  return null;
}

function classificationFromRef(
  ref: FeatureCatalogClassificationRef | null,
  system: DesignScopeCatalogItemClassification["system"]
): DesignScopeCatalogItemClassification | null {
  if (!ref || (!ref.number && !ref.title)) return null;
  return {
    id: ref.id ?? "",
    name: ref.title ?? "",
    code: ref.number ?? "",
    system
  };
}

function classificationFromApiRow(
  row: FeatureCatalogClassificationRow
): DesignScopeCatalogItemClassification | null {
  const system = mapCatalogClassificationSystem(row.system);
  if (!system || (!row.number && !row.title)) return null;
  const path = (row.path ?? [])
    .filter((node) => node?.number || node?.title)
    .map((node) => ({
      number: node.number ?? "",
      title: node.title ?? ""
    }));
  return {
    id: row.id ?? "",
    name: row.title ?? "",
    code: row.number ?? "",
    system,
    parentId: row.parent_id == null ? null : String(row.parent_id),
    ...(path.length ? { path } : {})
  };
}

/** Prefer `classifications[]` (includes path); fall back to UF/MF refs. */
export function toDesignScopeCatalogItem(
  row: FeatureCatalogItemRow
): DesignScopeCatalogItem {
  const fromArray = (row.classifications ?? [])
    .map(classificationFromApiRow)
    .filter((item): item is DesignScopeCatalogItemClassification =>
      Boolean(item)
    );
  const classifications =
    fromArray.length > 0
      ? fromArray
      : [
          classificationFromRef(row.uniformat, "UNIFORMAT"),
          classificationFromRef(row.masterformat, "MASTERFORMAT")
        ].filter((item): item is DesignScopeCatalogItemClassification =>
          Boolean(item)
        );

  return {
    id: row.id,
    title: row.title ?? "",
    description: row.description ?? "",
    number: row.sort_no == null ? "" : String(row.sort_no),
    classifications
  };
}

/**
 * Active items in one Design Scope catalog.
 * Search stays on the grid. This query takes only the catalog and feature type.
 */
export async function listDesignScopeCatalogItems(
  client: Pick<ApolloClient<unknown>, "query">,
  catalogId: string
): Promise<HTTPClientResponse> {
  try {
    const result = await client.query<{
      get_feature_catalog_items?: {
        items?: FeatureCatalogItemRow[] | null;
      } | null;
    }>({
      query: GET_FEATURE_CATALOG_ITEMS,
      variables: {
        feature_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
        catalog_id: catalogId
      },
      fetchPolicy: "network-only"
    });

    const graphqlError = result.errors?.[0];
    const rows = result.data?.get_feature_catalog_items?.items;
    if (graphqlError || !Array.isArray(rows)) {
      return {
        success: false,
        errorMsg: backendErrorMessage(undefined, graphqlError?.message),
        response: result
      };
    }

    const items = rows
      .filter((row) => row?.id && !row.deleted)
      .sort((left, right) => (left.sort_no ?? 0) - (right.sort_no ?? 0))
      .map(toDesignScopeCatalogItem);

    return {
      success: true,
      data: items,
      response: result
    };
  } catch (error) {
    return {
      success: false,
      errorMsg: backendErrorMessage(error),
      response: null
    };
  }
}

/**
 * Import a Design Scope catalog CSV at subscription level.
 * POST /features/10/catalogs/import
 */
export async function importDesignScopeCatalog(
  params: ImportDesignScopeCatalogParams
): Promise<HTTPClientResponse> {
  const formData = new FormData();
  const file = await fileWithNormalizedClassificationCodes(params.file);
  formData.append("file", file);
  formData.append("catalogName", params.catalogName);
  if (params.description) {
    formData.append("description", params.description);
  }

  const subscriptionToken = getAccessToken();
  return CIQClient.post({
    url: `/features/${DESIGN_SCOPE_FEATURE_TYPE_ID}/catalogs/import`,
    data: formData,
    showError: false,
    config: {
      headers: {
        Authorization: `Bearer ${subscriptionToken}`,
        "Content-Type": "multipart/form-data"
      }
    }
  });
}

export function triggerBlobFileDownload(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = href;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export function getFilenameFromContentDisposition(
  headerValue: string | undefined,
  fallback: string
): string {
  if (!headerValue) return fallback;
  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim());
    } catch {
      return utfMatch[1].trim();
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(headerValue);
  return plainMatch?.[1]?.trim() || fallback;
}
